import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Insight {
  kind: string;
  confidence: string;
  body: string;
  evidence: Record<string, unknown>;
}

interface Report {
  child: { id: string; nickname: string };
  weekOf: string;
  overview: {
    sessions: number;
    minutes: number;
    missionsCompleted: number;
    learningDays: number;
    ageYears: number;
    yearLevelLabel: string;
    skills: { mastered: number; proficient: number; developing: number; needsAttention: number };
    levels: { area: string; description: string }[];
    minutesPerMission: number | null;
    thin: boolean;
  };
  strengths: Insight[];
  attention: Insight[];
  patterns: Insight[];
  actions: { body: string; minutes: number; because: string }[];
  togetherCount: number;
  footer: string;
}

const RANGES = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
] as const;

/**
 * The dashboard.
 *
 * It exists to answer six questions with evidence: what is each child getting
 * better at, what are they struggling with, is the difficulty right, what
 * mistakes recur, what should they practise next, and what can I do with them
 * for five minutes.
 *
 * Where there is not enough data, it says so instead of drawing a confident
 * chart of three points.
 */
export function AdminDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState<(typeof RANGES)[number]['key']>('7d');

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard', range],
    queryFn: () => api<{ children: Report[] }>(`/admin/dashboard?range=${range}`),
  });

  const together = useQuery({
    queryKey: ['admin', 'together'],
    queryFn: () =>
      api<{ items: { id: string; childName: string; summary: string }[] }>('/admin/together'),
  });

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1>This week</h1>
          <p className="muted">Everything here comes from what actually happened.</p>
        </div>
        <button type="button" className="btn btn--outline" onClick={() => navigate('/admin')}>
          Settings
        </button>
      </div>

      <div className="tab-row" style={{ justifyContent: 'flex-start', marginBottom: 16 }}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className="chip"
            data-selected={range === r.key}
            onClick={() => setRange(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {(together.data?.items.length ?? 0) > 0 && (
        <section className="card card--warning">
          <h2>Waiting for you</h2>
          <ul className="plain-list">
            {together.data?.items.map((item) => (
              <li key={item.id}>
                <strong>{item.childName}</strong> — &ldquo;{item.summary}&rdquo;
              </li>
            ))}
          </ul>
          <button type="button" className="btn" onClick={() => navigate('/admin/writing')}>
            Read them together
          </button>
        </section>
      )}

      {dashboard.isPending && <p className="muted">Loading…</p>}

      {dashboard.data?.children.map((report) => (
        <ChildReport
          key={report.child.id}
          report={report}
          onDrill={() => navigate('/admin/children')}
        />
      ))}

      {dashboard.data?.children[0] && (
        <section className="card">
          <p className="muted" style={{ fontSize: 13 }}>
            {dashboard.data.children[0].footer}
          </p>
        </section>
      )}
    </div>
  );
}

function ChildReport({ report, onDrill }: { report: Report; onDrill: () => void }) {
  const o = report.overview;

  return (
    <section className="card">
      <div className="child-head">
        <div>
          <h2>{report.child.nickname}</h2>
          <p className="muted">{o.levels.map((l) => `${l.area}: ${l.description}`).join(' · ')}</p>
        </div>
      </div>

      {o.thin ? (
        <p className="gentle-note">
          Only {o.sessions} session{o.sessions === 1 ? '' : 's'} so far — not enough yet to say much
          about patterns. That is worth knowing too.
        </p>
      ) : null}

      <div className="stat-row">
        <Stat label="Adventures" value={o.missionsCompleted} />
        <Stat label="Days" value={o.learningDays} />
        <Stat label="Minutes" value={o.minutes} />
        <Stat label="Solid skills" value={o.skills.mastered + o.skills.proficient} />
        <Stat label="Still working" value={o.skills.developing} />
        <Stat
          label="Typical mission"
          value={o.minutesPerMission === null ? '—' : `${o.minutesPerMission} min`}
        />
      </div>

      <h3>Getting better at</h3>
      {report.strengths.length === 0 ? (
        <p className="muted">Nothing has moved far enough this week to call it out.</p>
      ) : (
        <ul className="plain-list">
          {report.strengths.map((s) => (
            <li key={s.body}>{s.body}</li>
          ))}
        </ul>
      )}

      <h3>Taking a while</h3>
      {report.attention.length === 0 ? (
        <p className="muted">Nothing is stuck at the moment.</p>
      ) : (
        <ul className="plain-list">
          {report.attention.map((s) => (
            <li key={s.body}>{s.body}</li>
          ))}
        </ul>
      )}

      <h3>Mistakes that keep coming up</h3>
      {report.patterns.length === 0 ? (
        <p className="muted">No repeated pattern yet.</p>
      ) : (
        <ul className="plain-list">
          {report.patterns.map((p) => (
            <li key={p.body}>
              <span
                className={`pill ${p.confidence === 'probable' ? 'pill--warn' : ''}`}
                style={{ marginRight: 8 }}
              >
                {p.confidence === 'probable'
                  ? 'pattern'
                  : p.confidence === 'hypothesis'
                    ? 'maybe'
                    : 'seen once'}
              </span>
              {p.body}
              <br />
              <small className="muted">
                {String(p.evidence.occurrences ?? '')} times across{' '}
                {String(p.evidence.sessions ?? '')} sessions
              </small>
            </li>
          ))}
        </ul>
      )}

      <h3>Five minutes with them</h3>
      <ul className="plain-list">
        {report.actions.map((action) => (
          <li key={action.body}>
            {action.body}
            <br />
            <small className="muted">
              {action.minutes} minutes · suggested because {action.because}
            </small>
          </li>
        ))}
      </ul>

      <button type="button" className="btn btn--quiet" onClick={onDrill}>
        Open {report.child.nickname}&rsquo;s profile
      </button>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}
